interface CountFieldOptions {
  min: number;
  max: number;
  unit(value: string): string;
  decreaseLabel: string;
  increaseLabel: string;
  invalidMessage: string;
  minimumMessage: string;
  maximumMessage: string;
  buttons?: boolean;
  stepValue?(current: number | undefined, direction: -1 | 1): number;
}

/** A directly editable count; the original input is the only submitted value. */
export function createCountField(input: HTMLInputElement, options: CountFieldOptions) {
  const element = document.createElement('div'); element.className = 'count-field';
  const decrease = document.createElement('button'); decrease.type = 'button'; decrease.textContent = '−';
  const increase = document.createElement('button'); increase.type = 'button'; increase.textContent = '+';
  decrease.setAttribute('aria-label', options.decreaseLabel); increase.setAttribute('aria-label', options.increaseLabel);
  decrease.className = increase.className = 'count-field-step';
  const unit = document.createElement('span'); unit.className = 'count-field-unit'; unit.setAttribute('aria-hidden', 'true');
  // Text + numeric inputmode keeps incomplete input intact and avoids wheel changes
  // and browser-specific exponent/decimal behavior of type=number.
  input.type = 'text'; input.inputMode = 'numeric'; input.pattern = '[0-9]*';
  input.setAttribute('role', 'spinbutton');
  input.setAttribute('aria-valuemin', String(options.min)); input.setAttribute('aria-valuemax', String(options.max));
  if (options.buttons !== false) element.append(decrease);
  element.append(input, unit);
  if (options.buttons !== false) element.append(increase);
  const disabled = () => input.matches(':disabled') || input.readOnly;
  const parsed = () => /^\d+$/.test(input.value) && Number.isSafeInteger(Number(input.value)) ? Number(input.value) : undefined;
  let disposed = false;
  let hold: { button: HTMLButtonElement; direction: -1 | 1; pointerId: number; started: number } | undefined;
  let repeatTimer: ReturnType<typeof setTimeout> | undefined;
  let suppressedClick: HTMLButtonElement | undefined;
  let suppressionTimer: ReturnType<typeof setTimeout> | undefined;
  const stopHold = () => { clearTimeout(repeatTimer); repeatTimer = undefined; hold = undefined; };
  const isVisible = (button: HTMLButtonElement) => {
    if (!button.isConnected) return false;
    for (let node: HTMLElement | null = button; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    }
    return true;
  };
  function error(): string {
    if (!input.value) return '';
    const value = parsed();
    if (value === undefined) return options.invalidMessage;
    if (value < options.min) return options.minimumMessage;
    if (value > options.max) return options.maximumMessage;
    return '';
  }
  function sync(): void {
    if (disposed) return;
    const value = parsed(), invalid = error();
    input.setCustomValidity(invalid);
    unit.textContent = options.unit(input.value);
    const usable = value !== undefined && !invalid;
    decrease.disabled = disabled() || !usable || value <= options.min;
    increase.disabled = disabled() || Boolean(input.value && !usable) || Boolean(usable && value >= options.max);
    if (hold && (hold.button.disabled || !isVisible(hold.button))) stopHold();
    if (usable) {
      input.setAttribute('aria-valuenow', String(value));
      input.setAttribute('aria-valuetext', `${value} ${unit.textContent}`);
    } else { input.removeAttribute('aria-valuenow'); input.removeAttribute('aria-valuetext'); }
  }
  function step(direction: -1 | 1): void {
    sync();
    if (disabled() || (direction === -1 ? decrease.disabled : increase.disabled)) return;
    const current = parsed();
    // Default + starts at one (or its minimum); specialized pickers supply stops.
    const next = options.stepValue ? options.stepValue(current, direction)
      : current === undefined ? Math.max(options.min, 1) : current + direction;
    if (next < options.min || next > options.max || !Number.isSafeInteger(next)) return;
    input.value = String(next); sync();
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  const click = (event: MouseEvent, direction: -1 | 1) => {
    // Pointer clicks following a repeat must not add a release-time step.
    // Zero-detail activation remains available to keyboards and assistive tools.
    if (event.detail > 0 && event.currentTarget === suppressedClick) {
      suppressedClick = undefined; event.preventDefault(); return;
    }
    step(direction);
  };
  const subtract = (event: MouseEvent) => click(event, -1), add = (event: MouseEvent) => click(event, 1);
  const repeat = () => {
    const active = hold;
    if (!active || disposed) return;
    sync();
    if (hold !== active) return;
    suppressedClick = active.button;
    step(active.direction);
    if (hold !== active) return;
    // Start at four edits/second, reaching twenty after four seconds of repeat.
    const interval = Math.max(50, 250 - (Date.now() - active.started - 1000) / 20);
    repeatTimer = setTimeout(repeat, interval);
  };
  const startHold = (event: PointerEvent, direction: -1 | 1) => {
    if (event.button !== 0 || event.isPrimary === false || hold || disposed) return;
    clearTimeout(suppressionTimer); suppressedClick = undefined;
    sync();
    const button = event.currentTarget as HTMLButtonElement;
    if (button.disabled) return;
    hold = { button, direction, pointerId: event.pointerId, started: Date.now() };
    repeatTimer = setTimeout(repeat, 1000);
  };
  const startSubtract = (event: PointerEvent) => startHold(event, -1);
  const startAdd = (event: PointerEvent) => startHold(event, 1);
  const endPointer = (event: PointerEvent) => {
    if (!hold && !suppressedClick) return;
    if (hold && event.pointerId !== hold.pointerId) return;
    stopHold();
    // Native click follows pointerup synchronously; expire suppression afterward.
    clearTimeout(suppressionTimer);
    suppressionTimer = setTimeout(() => { suppressedClick = undefined; }, 0);
  };
  const leave = (event: PointerEvent) => { if (event.pointerId === hold?.pointerId) stopHold(); };
  const move = (event: PointerEvent) => {
    if (!hold || event.pointerId !== hold.pointerId) return;
    // Touch pointers may be implicitly captured, suppressing pointerleave.
    const rect = hold.button.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) stopHold();
  };
  const visibility = () => { if (document.hidden) stopHold(); };
  const contextMenu = (event: Event) => event.preventDefault();
  const key = (event: KeyboardEvent) => {
    if (event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault(); step(event.key === 'ArrowUp' ? 1 : -1);
    }
  };
  const reset = () => { stopHold(); queueMicrotask(sync); };
  input.addEventListener('input', sync); input.addEventListener('change', sync); input.addEventListener('keydown', key);
  decrease.addEventListener('click', subtract); increase.addEventListener('click', add);
  decrease.addEventListener('pointerdown', startSubtract); increase.addEventListener('pointerdown', startAdd);
  for (const button of [decrease, increase]) {
    button.addEventListener('pointerleave', leave);
    button.addEventListener('lostpointercapture', leave);
    button.addEventListener('blur', stopHold);
    button.addEventListener('contextmenu', contextMenu);
  }
  document.addEventListener('pointerup', endPointer); document.addEventListener('pointercancel', endPointer);
  document.addEventListener('pointermove', move);
  document.addEventListener('visibilitychange', visibility); window.addEventListener('blur', stopHold);
  // Construction may precede insertion into a form; listen at document level.
  const onReset = (event: Event) => { if (event.target === input.form) reset(); };
  document.addEventListener('reset', onReset);
  const observer = new MutationObserver(records => {
    if (records.some(record => record.target instanceof Element && (record.target === input || record.target.contains(input)))) sync();
  });
  observer.observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['disabled', 'readonly'] });
  sync();
  return {
    element, sync, error,
    dispose() {
      if (disposed) return;
      stopHold(); clearTimeout(suppressionTimer);
      disposed = true; observer.disconnect(); document.removeEventListener('reset', onReset);
      input.removeEventListener('input', sync); input.removeEventListener('change', sync); input.removeEventListener('keydown', key);
      decrease.removeEventListener('click', subtract); increase.removeEventListener('click', add);
      decrease.removeEventListener('pointerdown', startSubtract); increase.removeEventListener('pointerdown', startAdd);
      for (const button of [decrease, increase]) {
        button.removeEventListener('pointerleave', leave);
        button.removeEventListener('lostpointercapture', leave);
        button.removeEventListener('blur', stopHold);
        button.removeEventListener('contextmenu', contextMenu);
      }
      document.removeEventListener('pointerup', endPointer); document.removeEventListener('pointercancel', endPointer);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('visibilitychange', visibility); window.removeEventListener('blur', stopHold);
      input.setCustomValidity('');
    },
  };
}
